import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClientMinimalList } from '../../../hooks/useClients';
import downArrow from '../../../assets/images/down-arrow.svg';

export const ClientSingleSelectWithSearch: React.FC<{
  label?: string;
  selectedClientId: number | null;
  onChange: (clientId: number | null) => void;
  placeholder?: string;
  showClearOption?: boolean;
  excludeClientIds?: number[];
  disabled?: boolean;
}> = ({
  label,
  selectedClientId,
  onChange,
  placeholder = 'All clients',
  showClearOption = true,
  excludeClientIds = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { data: clients, isFetching } = useClientMinimalList({
    search: search.trim() || undefined,
    all_statuses: true,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const selectedClient = useMemo(() => {
    if (!clients || selectedClientId == null) return null;
    return clients.find((c) => c.id === selectedClientId) ?? null;
  }, [clients, selectedClientId]);

  const buttonLabel = useMemo(() => {
    if (isFetching && !clients?.length) return 'Searching...';
    if (!selectedClient) return placeholder;
    return `${selectedClient.name} (#${selectedClient.id})`;
  }, [isFetching, clients, selectedClient, placeholder]);

  const handleSelect = (clientId: number | null) => {
    onChange(selectedClientId === clientId ? null : clientId);
    setIsOpen(false);
  };

  const displayClients = useMemo(
    () => (clients ?? []).filter((client) => !excludeClientIds.includes(client.id!)),
    [clients, excludeClientIds],
  );

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {label && <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className="focus:ring-blue-primary flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((p) => !p)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={disabled}
          data-testid="client-single-select-button"
        >
          <span className="truncate">{buttonLabel}</span>
          <img src={downArrow} alt="" className={`ml-2 h-2 w-2 transform transition ${isOpen ? '' : 'rotate-180'}`} />
        </button>
        {isOpen && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 px-2 py-2">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Type to search clients..."
                className="focus:ring-blue-primary w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {isFetching && !displayClients.length ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : displayClients.length ? (
                <>
                  {showClearOption && (
                    <button
                      type="button"
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        selectedClientId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => handleSelect(null)}
                    >
                      {placeholder}
                    </button>
                  )}
                  {displayClients.map((client) => {
                    const isSelected = selectedClientId === client.id;
                    return (
                      <button
                        key={client.id}
                        type="button"
                        className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        onClick={() => handleSelect(client.id)}
                      >
                        {client.name} (#{client.id})
                      </button>
                    );
                  })}
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {search.trim() ? 'No clients match your search.' : 'Type to search for clients.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
